/**
 * How a tool string is drawn — the API's numbering, drawn the way a string
 * hangs in the hole.
 *
 * POSITION 1 IS THE BIT. The API counts up from the bottom of the string, and
 * an add with no position goes to n + 1, above the last tool (api#883). The
 * screens used to list position 1 first under "Top to bottom" and call the
 * default add "Add at the bottom", which said the opposite of what the API
 * does: the tool the reader thought went on the bottom went on top.
 *
 * So the string is listed TOP FIRST — highest position at the top of the list,
 * position 1 (the bit) at the bottom — and every label says which end is which.
 *
 * "FROM BOTTOM" is the running length from the bit up to and including each
 * tool: the sum of its own length and every tool's below it. The API returns
 * the whole string's length (`total_length_ft`) and no per-tool running figure,
 * so this is the one sum the screen makes, and it agrees with the API's total
 * at the top tool by construction. A tool with no length on record makes its
 * own figure and every figure ABOVE it unknown (`null`, drawn `—`): a partial
 * sum that skipped it would understate the depth of everything over it.
 */

export interface StringMember {
  assembly_component_id: string;
  position: number;
  length_ft: number | null;
}

/** The members top first: highest position first, the bit (position 1) last. */
export function topFirst<T extends { position: number }>(members: readonly T[]): T[] {
  return [...members].sort((a, b) => b.position - a.position);
}

/** Running length from the bit, in stored feet, per assembly_component_id. */
export function lengthFromBottom(members: readonly StringMember[]): Map<string, number | null> {
  const out = new Map<string, number | null>();
  let sum: number | null = 0;
  for (const m of [...members].sort((a, b) => a.position - b.position)) {
    const len = typeof m.length_ft === 'number' && Number.isFinite(m.length_ft) ? m.length_ft : null;
    sum = sum === null || len === null ? null : sum + len;
    out.set(m.assembly_component_id, sum === null ? null : Math.round(sum * 100) / 100);
  }
  return out;
}

/** What a member's position means in words, for a label. */
export function positionNote(position: number, count: number): string {
  if (position === 1) return 'Bottom';
  if (position === count) return 'Top';
  return '';
}
